/**
 * Shared format policy for the picker and drop zone. Kept outside FileUpload
 * so the component module only exports a component for react-refresh.
 */
const PDF_TYPE = "application/pdf";

/**
 * Video formats a buyer can actually play, on any platform we support.
 *
 * Narrowed from eight on review. A resource here is purchasable, so accepting
 * an upload the buyer cannot open moves the failure onto the person who paid
 * for it, which is worse than refusing it at upload time.
 *
 * Dropped, and why:
 *
 * - `video/x-flv`. Flash, removed from every major browser years ago.
 * - `video/x-msvideo` (.avi). Does not play on macOS by default.
 * - `video/x-matroska` (.mkv). Not in Safari, so not on iOS either.
 * - `video/ogg`. Not in Safari.
 * - `video/mpeg`. Superseded, and byte sniffing cannot separate it cleanly
 *   from an MPEG transport stream.
 *
 * `video/quicktime` stays because it is what iOS cameras produce, so refusing
 * it would break the common mobile upload. The backend allowlist in
 * Echo-Backend `src/utils/uploadWhitelist.js` must match this list; the two
 * disagreeing is what turns a refusal into a 415 after the file is chosen.
 */
const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

/** Use the filename extension only when the browser reports no MIME type. */
const EXTENSION_FOR_TYPE = {
  "video/mp4": ".mp4",
  "video/webm": ".webm",
  "video/quicktime": ".mov",
  [PDF_TYPE]: ".pdf",
};

const isArticleEvent = (eventType) =>
  eventType === "Articles and Blogs" || eventType === "articles-and-blogs";

const isVideoEvent = (eventType) =>
  eventType === "Videos and Presentations" ||
  eventType === "videos-and-presentations";

/** The MIME types an event type accepts. Empty when the type is unrecognised. */
export const allowedTypesFor = (eventType) => {
  if (isArticleEvent(eventType)) return [PDF_TYPE];
  if (isVideoEvent(eventType)) return [...VIDEO_TYPES, PDF_TYPE];
  return [];
};

/**
 * The `accept` attribute value, or undefined for an unrecognised event type.
 *
 * Extensions are listed alongside the MIME types because an OS picker may match
 * on either, and `.pdf` is what this advertised before.
 */
export const getAllowedFormats = (eventType) => {
  const types = allowedTypesFor(eventType);
  if (!types.length) return undefined;
  const extensions = types.map((type) => EXTENSION_FOR_TYPE[type]);
  return [...types, ...extensions].join(", ");
};

const extensionOf = (name = "") => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
};

const mimeTypeFor = (file) => {
  if (file?.type) return file.type;
  const extension = extensionOf(file?.name);
  return Object.entries(EXTENSION_FOR_TYPE).find(
    ([, supportedExtension]) => supportedExtension === extension,
  )?.[0];
};

/** Carry the extension fallback through to previews and multipart uploads. */
export const withUploadMimeType = (file) => {
  const type = mimeTypeFor(file);
  if (!file || file.type || !type) return file;
  return new File([file], file.name, { type, lastModified: file.lastModified });
};

/**
 * Whether this event type accepts the file.
 *
 * An unrecognised event type advertises no `accept`, so it keeps accepting
 * anything rather than starting to refuse everything.
 */
export const isAllowedUpload = (eventType, file) => {
  const allowed = allowedTypesFor(eventType);
  if (!allowed.length) return true;
  return allowed.includes(mimeTypeFor(file));
};

/** What to tell someone who picked something else. */
export const describeAllowed = (eventType) =>
  isArticleEvent(eventType) ? "a PDF" : "a video or a PDF";
